'use client';

import type { MouseEventHandler, ReactElement } from 'react';
import React from 'react';
import { mergeProps } from 'react-aria';
import { useSharedHoverContext } from 'components/SharedHover';
import { baseTextStyles, textStyles } from 'components/Text/text-variants';
import { focusStyles, hoverTransition } from 'mixins/interactive';
import styled, { css } from 'styled-components';
import isValidProp from '@emotion/is-prop-valid';
import type { PressEvent } from '@react-types/shared';
import type { IconProps } from '@frameio/vapor-icons';
import { theme } from '@frameio/vapor-theme';
import { containedVariant } from './contained';
import { ghostVariant } from './ghost';
import { IconAfter, IconBefore } from './Icons';
import { linkVariant } from './link';
import type { ButtonColor, ButtonSize } from './types';

/**
 * @deprecated Avoid using this constant as this module is deprecated.
 */
export const buttonPaddings = {
  small: {
    x: 8,
    y: 0,
  },
  medium: {
    x: 12,
    y: 0,
  },
} as const;

/**
 * @deprecated Avoid using this constant as this module is deprecated.
 */
export const buttonIconSizes = {
  small: 16,
  medium: 24,
} as const;

/**
 * @deprecated Avoid using this constant as this module is deprecated.
 */
export const buttonBoxSizes = {
  small: 24,
  medium: 32,
} as const;

/**
 * @deprecated Avoid using this constant as this module is deprecated.
 */
export const sizes = {
  small: css`
    ${textStyles.linkSmall};
    height: ${theme.spaces[buttonBoxSizes.small]};
    line-height: ${theme.spaces[buttonBoxSizes.small]};
    padding: ${theme.spaces[buttonPaddings.small.y]}
      ${theme.spaces[buttonPaddings.small.x]};
  `,
  medium: css`
    ${textStyles.link};
    height: ${theme.spaces[buttonBoxSizes.medium]};
    line-height: ${theme.spaces[buttonBoxSizes.medium]};
    padding: ${theme.spaces[buttonPaddings.medium.y]}
      ${theme.spaces[buttonPaddings.medium.x]};
  `,
};

/**
 * @deprecated Avoid using this constant as this module is deprecated.
 */
export const buttonVariants = {
  link: linkVariant(),
  contained: containedVariant(),
  ghost: ghostVariant(),
};

/**
 * @deprecated Avoid using this type as this module is deprecated.
 */
export type ButtonVariant = keyof typeof buttonVariants;

/**
 * @deprecated `ButtonProps` has been deprecated along with the `Button`
 * component. Use `Button2` or `InlineButton` for link variants instead.
 */
export type ButtonProps = {
  as?: any;
  block?: boolean;
  color?: ButtonColor;
  iconAfter?: ReactElement<IconProps>;
  iconBefore?: ReactElement<IconProps>;
  size?: ButtonSize;
  to?: any;
  variant?: ButtonVariant;
  /** onClick handler that supports React click event handlers as well as
   * event handlers coming from the React Spectrum library that handle both
   * click and press events for mobile. However, Styled Components does not
   * support this union type, so we coerce back to MouseEventHandler later. */
  onClick?: MouseEventHandler<HTMLButtonElement> | ((e: PressEvent) => void);
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> &
  Omit<React.AnchorHTMLAttributes<HTMLButtonElement>, 'onClick'>;

/**
 * @deprecated use `Button2` or `InlineButton` for 'link' variants instead. This
 * component will be removed in a future release.
 */
export const Button = React.forwardRef(
  (
    {
      children,
      size = 'medium',
      color = 'secondary',
      disabled = false,
      type: typeProp,
      variant = 'contained',
      iconBefore,
      iconAfter,
      block = false,
      as,
      onClick,
      ...other
    }: ButtonProps,
    ref: any
  ) => {
    const isLink = Boolean(other!.href);

    // Only apply "type='button'" when the dom element is actually a button.
    const type =
      isLink || typeProp || (as && as !== 'button') ? typeProp : 'button';

    const { onEnterTarget, onLeaveTarget, enabled } = useSharedHoverContext();

    function onMouseEnter(e: React.MouseEvent) {
      onEnterTarget(e.currentTarget.getBoundingClientRect());
    }

    function onMouseLeave() {
      onLeaveTarget();
    }

    /** Fix inconsistent focusing behavior for buttons. */
    function onMouseDown(e: React.MouseEvent<HTMLButtonElement>) {
      e.currentTarget.focus();
    }

    return (
      <StyledButton
        ref={ref}
        $block={block}
        $color={color}
        $sharedHoverEnabled={enabled && variant === 'ghost'}
        $size={size}
        $variant={variant}
        as={isLink ? 'a' : as}
        disabled={disabled}
        onClick={onClick as MouseEventHandler<HTMLButtonElement>}
        type={type}
        {...mergeProps(
          other,
          { onMouseDown },
          // @ts-expect-error FIXME(strictNullChecks)
          variant === 'ghost' && {
            onMouseEnter,
            onMouseLeave,
          }
        )}
      >
        {iconBefore && React.isValidElement(iconBefore) && (
          <IconBefore>
            {React.cloneElement(iconBefore, {
              'aria-hidden': true,
              size: iconBefore.props.size ?? buttonIconSizes[size],
            })}
          </IconBefore>
        )}
        {children}
        {iconAfter && React.isValidElement(iconAfter) && (
          <IconAfter>
            {React.cloneElement(iconAfter, {
              'aria-hidden': true,
              size: iconAfter.props.size ?? buttonIconSizes[size],
            })}
          </IconAfter>
        )}
      </StyledButton>
    );
  }
);

/**
 * @deprecated Avoid using this constant as this module is deprecated.
 */
export const buttonReset = css`
  &[type='button'] {
    -webkit-appearance: none;
  }
  -webkit-appearance: none;
  -webkit-tap-highlight-color: transparent;
  background: none;
  border: none;
  box-shadow: none;
  box-sizing: border-box;
  cursor: pointer;
  padding: 0;
  text-align: center;
  text-decoration: none;
  user-select: none;
  white-space: nowrap;
`;

type StyledButtonProps = {
  $size: ButtonSize;
  $color: ButtonColor;
  $variant: ButtonVariant;
  $sharedHoverEnabled: boolean;
  $block: boolean;
  disabled: boolean;
};

export const StyledButton = styled.button.withConfig({
  shouldForwardProp: (prop) => isValidProp(prop),
})<StyledButtonProps>`
  ${buttonReset};
  ${baseTextStyles as any};
  align-items: center;
  border-radius: ${theme.radii[6]};
  display: inline-flex;
  justify-content: center;
  min-width: 0;
  outline: none;
  position: relative;
  vertical-align: middle;
  ${hoverTransition};
  ${(p) => sizes[p.$size]};
  ${(p) => buttonVariants[p.$variant]};
  ${focusStyles};
  ${(p) =>
    p.$block &&
    `
    width: 100%;
  `}
  ${(p) =>
    p.$sharedHoverEnabled &&
    `
      will-change: transform;
    `}
`;
